import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, setDoc, serverTimestamp, updateDoc, increment, getDoc } from 'firebase/firestore';
import { getBeltForSessions } from '../constants/beltRanks';
import { playChime, ensureAudioUnlocked, playButtonClick, playTone } from '../utils/audio';
import BeltMilestoneAnimation from '../components/BeltMilestoneAnimation';

export default function LogSessionPage({ user, darkMode, onCancel, onSuccess }) {
  const formatEU = (iso) => {
    if (!iso || typeof iso !== 'string') return '';
    const parts = iso.split('-');
    if (parts.length !== 3) return iso;
    const [y, m, d] = parts;
    return `${d}/${m}/${y}`;
  };
  const getTodayFinland = () => {
    const now = new Date();
    const finlandTime = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Helsinki', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
    return finlandTime;
  };

  const [step, setStep] = useState('date');
  const [selectedDate, setSelectedDate] = useState(getTodayFinland());
  const [selectedWorkouts, setSelectedWorkouts] = useState([]);
  const [customWorkout, setCustomWorkout] = useState('');
  const [intensity, setIntensity] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [beltMilestone, setBeltMilestone] = useState(null);
  const [lastSessionData, setLastSessionData] = useState(null);

  const presetWorkouts = ['Gym/Pull', 'Gym/Push', 'Gym/Legs', 'Gym/Full Body', 'Gym/Upper Body', 'BJJ', 'Basketball', 'Swimming'];
  const getWordCount = (name) => name.replace('Gym/', '').trim().split(/\s+/).length;
  const sortAlpha = (a, b) => a.localeCompare(b);
  const gymPresets = presetWorkouts.filter(workout => workout.startsWith('Gym/'));
  const otherPresets = presetWorkouts.filter(workout => !workout.startsWith('Gym/'));
  const gymSingle = gymPresets.filter(w => getWordCount(w) === 1).sort(sortAlpha);
  const gymMulti = gymPresets.filter(w => getWordCount(w) > 1).sort(sortAlpha);
  const otherSingle = otherPresets.filter(w => getWordCount(w) === 1).sort(sortAlpha);
  const otherMulti = otherPresets.filter(w => getWordCount(w) > 1).sort(sortAlpha);

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '16px',
    fontFamily: 'inherit',
    marginBottom: '16px',
    textAlign: 'center',
    ...(darkMode && { backgroundColor: '#374151', borderColor: '#4b5563', color: '#ffffff' })
  };





  const toggleWorkout = (name) => {
    setSelectedWorkouts(prev =>
      prev.includes(name) ? prev.filter(w => w !== name) : [...prev, name]
    );
  };

  const addCustom = () => {
    if (customWorkout.trim() && !selectedWorkouts.includes(customWorkout)) {
      toggleWorkout(customWorkout);
      setCustomWorkout('');
    }
  };

  const handleLog = async () => {
    if (!user?.uid) {
      alert('Not signed in');
      return;
    }

    setIsSaving(true);

    // Check if session already exists for this date
    try {
      const sessionRef = doc(db, 'users', user.uid, 'sessions', selectedDate);
      const sessionSnap = await getDoc(sessionRef);
      
      if (sessionSnap.exists()) {
        const confirmUpdate = window.confirm(`You already logged a session on ${selectedDate === getTodayFinland() ? 'Today' : selectedDate}. Do you want to update it?`);
        if (!confirmUpdate) {
          return;
        }
      }
    } catch (err) {
      console.error('Failed to check existing session', err);
    }

    const sessionData = {
      date: selectedDate,
      workouts: selectedWorkouts.map(w => ({ name: w, intensity: intensity[w] || 5 })),
      totalWorkouts: selectedWorkouts.length,
      timestamp: new Date().toISOString(),
      createdAt: serverTimestamp()
    };

    // Save to Firestore with date as ID
    try {
      const sessionRef = doc(db, 'users', user.uid, 'sessions', selectedDate);
      const isNew = !(await getDoc(sessionRef)).exists();
      
      await setDoc(sessionRef, sessionData);

      let hasBeltMilestone = false;

      // Only increment stats if it's a new session
      if (isNew) {
        const profileRef = doc(db, 'profiles', user.uid);
        const profileSnap = await getDoc(profileRef);
        const currentSessions = profileSnap.data()?.sessionCount || 0;
        const newSessionCount = currentSessions + 1;
        const oldBelt = getBeltForSessions(currentSessions);
        const newBelt = getBeltForSessions(newSessionCount);
        
        await updateDoc(profileRef, {
          sessionCount: increment(1),
          workoutCount: increment(selectedWorkouts.length),
          beltRank: newBelt.name
        });

        // Check if belt rank changed and trigger milestone animation
        if (oldBelt.name !== newBelt.name) {
          hasBeltMilestone = true;
          setBeltMilestone({ belt: newBelt, sessionCount: newSessionCount });
          setLastSessionData(sessionData);
        }
      }

      // Also keep in localStorage for offline
      const sessions = JSON.parse(localStorage.getItem('fitBudly_sessions') || '{}');
      sessions[selectedDate] = sessionData;
      localStorage.setItem('fitBudly_sessions', JSON.stringify(sessions));

      await playChime();
      // Keep loading visible while chime plays (~650ms)
      await new Promise(resolve => setTimeout(resolve, 650));
      
      // NOW clear loading screen completely
      setIsSaving(false);
      
      // Wait a bit for loading to disappear from DOM
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // WAIT 1.5 seconds before showing animation (let the moment breathe)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // If NO milestone, go home immediately
      if (!hasBeltMilestone) {
        setTimeout(() => onSuccess(sessionData), 50);
      }
      // If milestone, animation will call onSuccess when button is pressed
    } catch (err) {
      console.error('Failed to save session', err);
      alert('Failed to save session: ' + err.message);
      setIsSaving(false);
    }
  };

  const handleLogWithAudio = async () => {
    await ensureAudioUnlocked();
    return handleLog();
  };

  const getIntensityFrequency = (value) => {
    const minFreq = 180;
    const maxFreq = 900;
    const clamped = Math.max(0, Math.min(10, value));
    return minFreq + (maxFreq - minFreq) * (clamped / 10);
  };

  const gymSelected = selectedWorkouts.filter(workout => workout.startsWith('Gym/'));
  const otherSelected = selectedWorkouts.filter(workout => !workout.startsWith('Gym/'));

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'transparent',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      color: darkMode ? '#f9fafb' : '#111827'
    }}>
      {/* Belt Milestone Animation */}
      {beltMilestone && (
        <BeltMilestoneAnimation
          user={user}
          belt={beltMilestone.belt}
          onComplete={() => {
            setBeltMilestone(null);
            // Go home after animation button is clicked
            setTimeout(() => onSuccess(lastSessionData), 50);
          }}
        />
      )}
      
      {isSaving && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #374151',
            borderTop: '4px solid #22c55e',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{
            marginTop: '24px',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#22c55e',
            textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
          }}>
            Saving Session...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
      <div style={{ maxWidth: '420px', width: '100%', textAlign: 'center' }}>
        {step === 'date' && (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: darkMode ? '#ffffff' : '#1f2937',
              marginBottom: '32px',
              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
            }}>
              Select Date
            </h2>
            <div style={{ position: 'relative', marginBottom: '24px', height: '48px' }}>
              <input
                type="date"
                value={selectedDate}
                max={getTodayFinland()}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  ...inputStyle,
                  color: 'transparent',
                  WebkitTextFillColor: 'transparent',
                  cursor: 'pointer',
                  height: '100%'
                }}
              />
              <span style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                color: darkMode ? '#ffffff' : '#1f2937',
                fontSize: '16px',
                fontWeight: '500',
                letterSpacing: '0.5px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}>
                {selectedDate === getTodayFinland() ? '📅 Today' : `📅 ${formatEU(selectedDate)}`}
              </span>
            </div>
            <button
              onClick={() => { playButtonClick(); setStep('workouts'); }}
              style={{
                width: 'auto',
                padding: '12px 24px',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                margin: '0 auto 16px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}
            >
              Next
            </button>
            <button
              onClick={() => { playButtonClick(); onCancel(); }}
              style={{
                width: '120px',
                margin: '16px auto 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                color: '#ffffff',
                backgroundColor: '#3b82f6',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '12px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}
            >
              ← Back
            </button>
          </div>
        )}

        {step === 'workouts' && (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: darkMode ? '#ffffff' : '#1f2937',
              marginBottom: '8px',
              textAlign: 'center',
              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
            }}>
              Select Workouts
            </h2>
            <p style={{
              color: '#f9fafb',
              marginBottom: '24px',
              fontSize: '14px',
              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
            }}>
              Choose one or multiple
            </p>

            {/* All Workouts - Gym Presets */}
            <div style={{ marginBottom: '24px' }}>
              <p style={{
                fontSize: '12px',
                fontWeight: 'bold',
                color: darkMode ? '#93c5fd' : '#1f2937',
                marginBottom: '8px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}>
                Gym
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  justifyContent: 'center'
                }}>
                  {gymSingle.map(workout => {
                  const hasGymSelected = selectedWorkouts.some(w => w.startsWith('Gym/'));
                  const isDisabled = hasGymSelected && !selectedWorkouts.includes(workout);
                  const displayName = workout.replace('Gym/', '');
                  return (
                    <button
                      key={workout}
                      onClick={() => { if (!isDisabled) { playButtonClick(); toggleWorkout(workout); } }}
                      disabled={isDisabled}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        border: 'none',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        backgroundColor: selectedWorkouts.includes(workout) ? '#ef4444' : isDisabled ? '#9ca3af' : '#3b82f6',
                        color: selectedWorkouts.includes(workout) ? '#ffffff' : isDisabled ? '#d1d5db' : '#ffffff',
                        opacity: isDisabled ? 0.5 : 1,
                        textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                      }}
                    >
                      {displayName}
                    </button>
                  );
                  })}
                </div>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  justifyContent: 'center'
                }}>
                  {gymMulti.map(workout => {
                    const hasGymSelected = selectedWorkouts.some(w => w.startsWith('Gym/'));
                    const isDisabled = hasGymSelected && !selectedWorkouts.includes(workout);
                    const displayName = workout.replace('Gym/', '');
                    return (
                      <button
                        key={workout}
                        onClick={() => { if (!isDisabled) { playButtonClick(); toggleWorkout(workout); } }}
                        disabled={isDisabled}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '20px',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          border: 'none',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          backgroundColor: selectedWorkouts.includes(workout) ? '#ef4444' : isDisabled ? '#9ca3af' : '#3b82f6',
                          color: selectedWorkouts.includes(workout) ? '#ffffff' : isDisabled ? '#d1d5db' : '#ffffff',
                          opacity: isDisabled ? 0.5 : 1,
                          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                        }}
                      >
                        {displayName}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Other Sports */}
            <div style={{ marginBottom: '24px' }}>
              <p style={{
                fontSize: '12px',
                fontWeight: 'bold',
                color: darkMode ? '#f9fafb' : '#1f2937',
                marginBottom: '8px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}>
                Other Sports
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  justifyContent: 'center'
                }}>
                  {otherSingle.map(workout => (
                    <button
                      key={workout}
                      onClick={() => { playButtonClick(); toggleWorkout(workout); }}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        border: 'none',
                        cursor: 'pointer',
                        backgroundColor: selectedWorkouts.includes(workout) ? '#ef4444' : '#3b82f6',
                        color: selectedWorkouts.includes(workout) ? '#ffffff' : '#ffffff',
                        textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                      }}
                    >
                      {workout}
                    </button>
                  ))}
                </div>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  justifyContent: 'center'
                }}>
                  {otherMulti.map(workout => (
                    <button
                      key={workout}
                      onClick={() => { playButtonClick(); toggleWorkout(workout); }}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        border: 'none',
                        cursor: 'pointer',
                        backgroundColor: selectedWorkouts.includes(workout) ? '#ef4444' : '#3b82f6',
                        color: selectedWorkouts.includes(workout) ? '#ffffff' : '#ffffff',
                        textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                      }}
                    >
                      {workout}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Custom */}
            <div style={{ marginBottom: '24px' }}>
              <p style={{
                fontSize: '12px',
                fontWeight: 'bold',
                color: darkMode ? '#ffffff' : '#1f2937',
                marginBottom: '8px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}>
                Add Custom
              </p>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <input
                  type="text"
                  placeholder="Custom workout..."
                  value={customWorkout}
                  onChange={(e) => setCustomWorkout(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addCustom()}
                  style={{
                    ...inputStyle,
                    marginBottom: 0,
                    flex: 1,
                    minWidth: '200px'
                  }}
                />
                <button
                  onClick={() => { playButtonClick(); addCustom(); }}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#4b5563',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '18px',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}
                >
                  +
                </button>
              </div>
            </div>

            {/* Next Button - Only enabled if workouts are selected */}
            <div style={{ display: 'flex', gap: '12px', flexDirection: 'column', alignItems: 'center' }}>
              <button
                onClick={() => { playButtonClick(); setStep('intensity'); }}
                disabled={selectedWorkouts.length === 0}
                style={{
                  width: 'auto',
                  alignSelf: 'center',
                  minWidth: '140px',
                  padding: '12px 24px',
                  backgroundColor: selectedWorkouts.length > 0 ? '#3b82f6' : '#d1d5db',
                  color: selectedWorkouts.length > 0 ? '#ffffff' : '#9ca3af',
                  fontWeight: 'bold',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: selectedWorkouts.length > 0 ? 'pointer' : 'not-allowed',
                  fontSize: '16px',
                  textShadow: selectedWorkouts.length > 0 ? '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' : 'none'
                }}
              >
                Next
              </button>
              <button
                onClick={() => { playButtonClick(); setStep('date'); }}
                style={{
                  width: 'auto',
                  alignSelf: 'center',
                  minWidth: '140px',
                  padding: '12px 24px',
                  backgroundColor: darkMode ? '#374151' : '#e5e7eb',
                  color: darkMode ? '#ffffff' : '#1f2937',
                  fontWeight: 'bold',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                }}
              >
                Back
              </button>
            </div>
          </div>
        )}

        {step === 'intensity' && selectedWorkouts.length > 0 && (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: darkMode ? '#ffffff' : '#1f2937',
              marginBottom: '8px',
              textAlign: 'center',
              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
            }}>
              Set Intensity
            </h2>
            <p style={{
              color: '#f9fafb',
              marginBottom: '24px',
              fontSize: '14px',
              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
            }}>
              How hard did you push?
            </p>

            <div style={{
              backgroundColor: darkMode ? '#1f2937' : '#ffffff',
              padding: '16px',
              borderRadius: '8px',
              border: '2px solid ' + (darkMode ? '#374151' : '#e5e7eb'),
              marginBottom: '24px'
            }}>
              <p style={{
                fontSize: '14px',
                fontWeight: 'bold',
                color: darkMode ? '#ffffff' : '#1f2937',
                marginBottom: '16px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}>
                Intensity (1-10)
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {gymSelected.length > 0 && (
                  <p style={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: darkMode ? '#f9fafb' : '#1f2937',
                    marginBottom: '4px',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}>
                    Gym
                  </p>
                )}
                {gymSelected.map(workout => (
                  <div key={workout} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: '1px solid ' + (darkMode ? '#374151' : '#e5e7eb'),
                    backgroundColor: darkMode ? '#111827' : '#f9fafb'
                  }}>
                    <span style={{ color: darkMode ? '#ffffff' : '#1f2937', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px', textAlign: 'center' }}>
                      {workout.startsWith('Gym/') ? workout.replace('Gym/', '') : workout}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                      <span style={{ fontSize: '20px' }}>🔥</span>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={intensity[workout] || 5}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          setIntensity(prev => ({ ...prev, [workout]: value }));
                          playTone(getIntensityFrequency(value), 0.06, 0.05);
                        }}
                        style={{ width: '160px' }}
                      />
                      <span style={{
                        width: '24px',
                        textAlign: 'center',
                        color: darkMode ? '#ffffff' : '#1f2937',
                        textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                      }}>
                        {intensity[workout] || 5}
                      </span>
                    </div>
                  </div>
                ))}
                {otherSelected.length > 0 && (
                  <p style={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: darkMode ? '#f9fafb' : '#1f2937',
                    marginTop: '8px',
                    marginBottom: '4px',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}>
                    Other Sports
                  </p>
                )}
                {otherSelected.map(workout => (
                  <div key={workout} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: '1px solid ' + (darkMode ? '#374151' : '#e5e7eb'),
                    backgroundColor: darkMode ? '#111827' : '#f9fafb'
                  }}>
                    <span style={{ color: darkMode ? '#ffffff' : '#1f2937', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px', textAlign: 'center' }}>
                      {workout.startsWith('Gym/') ? workout.replace('Gym/', '') : workout}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                      <span style={{ fontSize: '20px' }}>🔥</span>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={intensity[workout] || 5}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          setIntensity(prev => ({ ...prev, [workout]: value }));
                          playTone(getIntensityFrequency(value), 0.06, 0.05);
                        }}
                        style={{ width: '160px' }}
                      />
                      <span style={{
                        width: '24px',
                        textAlign: 'center',
                        color: darkMode ? '#ffffff' : '#1f2937',
                        textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                      }}>
                        {intensity[workout] || 5}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Save and Back Buttons */}
            <div style={{ display: 'flex', gap: '12px', flexDirection: 'column', alignItems: 'center' }}>
              <button
                onClick={() => { if (selectedWorkouts.length > 0) playButtonClick(); handleLogWithAudio(); }}
                disabled={selectedWorkouts.length === 0 || isSaving}
                style={{
                  width: 'auto',
                  minWidth: '160px',
                  padding: '12px 24px',
                  backgroundColor: selectedWorkouts.length > 0 ? '#22c55e' : '#d1d5db',
                  color: selectedWorkouts.length > 0 ? '#ffffff' : '#9ca3af',
                  fontWeight: 'bold',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: selectedWorkouts.length > 0 && !isSaving ? 'pointer' : 'not-allowed',
                  fontSize: '16px',
                  marginBottom: '16px',
                  textShadow: selectedWorkouts.length > 0 ? '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' : 'none'
                }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => { playButtonClick(); setStep('workouts'); }}
                style={{
                  width: 'auto',
                  minWidth: '140px',
                  padding: '12px 24px',
                  backgroundColor: darkMode ? '#374151' : '#e5e7eb',
                  color: darkMode ? '#ffffff' : '#1f2937',
                  fontWeight: 'bold',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                }}
              >
                Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}